import React, { useEffect, useState, useRef } from "react";
import { Container, Nav, NavItem, StyledButton, StyledWrap } from "./styles";
import { useAddItemToCart, useCart, useUpsells } from "src/context/siteContext";
import { encode } from "shopify-gid";
import { isSSR } from "src/utils/ssr";
import _ from "lodash";
import { SanityImage } from "src/interfaces/sanity";
import { ProductUpsells } from "src/interfaces/sanity";
import { eventData } from 'src/utils/dataLayer';
import tryGlobal from "src/lambda/tryGlobal";
import trackKlavyioAddedToCart from "src/utils/mapKlaviyoPayload";
import { useCachedElementDimensions } from "src/utils/getDimensions";

async function shopifyFetch({ query, variables }: { query: string; variables?: any }) {
  const endpoint = `https://${process.env.GATSBY_SHOPIFY_STORE}/api/2023-10/graphql.json`;
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": process.env.GATSBY_SHOPIFY_STOREFRONT_TOKEN || "",
    },
    body: JSON.stringify({ query, variables }),
  });
  return response.json();
}

const PRODUCT_VARIANT_QUERY = `
  query getProduct($id: ID!) {
    product(id: $id) {
      id
      variants(first: 100) {
        edges {
          node {
            id
            title
            availableForSale
            sku
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            image {
              url
              altText
            }
          }
        }
      }
    }
  }
`;

interface Props {
  mainImage: SanityImage;
  slug?: {
    current: string;
  };
  nav: [
    {
      _key: string;
      title: string;
      sectionId: string;
    }
  ];
  id: string,
  activePack: any;
  quantity: any;
  defaultPrice: any;
  productId: any;
  notificationBar: boolean;
  mobilePhoto?: SanityImage;
  atcButton?: {
    caption?: string;
    price?: boolean;
  };
  title?: string;
  upsells: Array<ProductUpsells>;
}

const SecondaryNav = ({
  mainImage,
  slug,
  id,
  nav,
  activePack,
  quantity,
  defaultPrice,
  productId,
  notificationBar,
  mobilePhoto,
  atcButton,
  title,
  upsells,
}: Props) => {
  const { checkout } = useCart();
  const { addItemToCart } = useAddItemToCart();
  const { openUpsells } = useUpsells();
  const [adding, setAdding] = useState(false as boolean);
  const [price, setPrice] = useState(defaultPrice);
  const [available, setAvailable] = useState(false);
  const [variants, setVariants] = useState([]);
  const [activeVariantId, setActiveVariantId] = useState("" as string);
  const [compareAtPrice, setCompareAtPrice] = useState(
    undefined as string | undefined
  );
  const [check, setCheck] = useState(true);
  const [show, setShow] = useState(false);

  const navRef = useRef(null);
  const { offsetBottom: heroBottom } = useCachedElementDimensions("product-hero");
  const { height: headerHeight } = useCachedElementDimensions("secondaryNav");

  useEffect(() => {
    if (check) {
      const shopifyId = encode("Product", productId, {
        accessToken: process.env.GATSBY_SHOPIFY_STOREFRONT_TOKEN,
      });

      const fetchProduct = async () => {
        try {
          const { data } = await shopifyFetch({
            query: PRODUCT_VARIANT_QUERY,
            variables: { id: shopifyId }
          });
          
          const product = data?.product;
          
          if(!product) return;

          const decodedVariants = product.variants.edges.map(({ node }: any) => {
            return {
              ...node,
              available: node.availableForSale,
              price: node.price.amount,
              compareAtPrice: node.compareAtPrice?.amount,
              image: node.image ? { src: node.image.url, ...node.image } : null
            };
          });

          setVariants(decodedVariants);
          const firstVariant = decodedVariants[0];
          
          if (firstVariant) {
            setActiveVariantId(firstVariant.id as string);
            setAvailable(firstVariant.available);
            if (firstVariant.compareAtPrice) {
              setCompareAtPrice(firstVariant.compareAtPrice);
            }
          }

          setCheck(false);
        } catch (error) {
          console.error("Error fetching product in SecondaryNav", error);
        }
      };
      fetchProduct();
    }
  }, [check, productId]);

  useEffect(() => {
    if (isSSR || !navRef.current) return;
    
    const throttledScroll = _.throttle(() => {
      const scrollPos = window.scrollY + headerHeight;
      setShow(scrollPos >= heroBottom);
    }, 300);

    window.addEventListener("scroll", throttledScroll);
    return () => window.removeEventListener("scroll", throttledScroll);
  }, [heroBottom, headerHeight]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!available) {
      return;
    }

    const item = {
      name: title,
      id: variants[0]?.sku,
      image: variants[0]?.image?.src,
      product_id: id,
      price: parseFloat(price),
      brand: 'GroovePillows',
      variant: variants[0]?.title,
      quantity: quantity,
    };

    setAdding(true);

    dataLayer?.push({
      ...eventData('add_to_cart'),
      ecommerce: {
        value: item.price * item.quantity,
        currencyCode: (process.env.GATSBY_CURRENCY_SYMBOL == "$") ? "USD" : "GBP",
        items: [item],
      }
    });

    const attributes = [];
    const upsellEnabled = upsells?.enabled && upsells?.productList.length > 0;

    await addItemToCart(activeVariantId, quantity, attributes, upsellEnabled);

    // TriplePixel('AddToCart', { item: variants[0]?.sku, q: quantity });
    tryGlobal('fbq', 'track', 'AddToCart', {
      content_name: title,
      content_ids: [item.id],
      content_type: 'product_group',
      value: parseFloat(item.price.toString()),
      currency: (process.env.GATSBY_CURRENCY_SYMBOL == "$") ? "USD" : "GBP",
    });

    trackKlavyioAddedToCart(checkout, item);

    setAdding(false);

    if (!upsellEnabled) return;

    openUpsells({
      boughtItem: {
        id: productId,
        title,
        quantity,
        price,
        imageRef: mainImage,
      },
      productList: upsells?.productList,
    });
  };

  const getButtonCaption = () => {
    if (!atcButton?.caption) {
      if (activePack)
        return `Buy for ${process.env.GATSBY_CURRENCY_SYMBOL +
          activePack.price}`;

      return `Buy for ${process.env.GATSBY_CURRENCY_SYMBOL +
        parseFloat(price * quantity).toFixed(2)}`;
    }

    if (atcButton?.price) {
      if (activePack)
        return `${atcButton?.caption} ${process.env.GATSBY_CURRENCY_SYMBOL +
          activePack.price}`;

      return `${atcButton?.caption} ${process.env.GATSBY_CURRENCY_SYMBOL +
        parseFloat(price * quantity).toFixed(2)}`;
    }

    return `${atcButton?.caption}`;
  };

  return (
    <Container ref={navRef} show={show} notificationBar={notificationBar} id="secondaryNav">
      <StyledWrap>
        <Nav>
          {nav?.map((item) => (
            <NavItem key={item?._key} href={`#${item?.sectionId}`}>
              {item?.title}
            </NavItem>
          ))}
        </Nav>
        <StyledButton small={true} onClick={handleSubmit}>
          <span>{adding ? "Adding to cart" : getButtonCaption()}</span>
        </StyledButton>
      </StyledWrap>
    </Container>
  );
};

export default SecondaryNav;
